/* eslint-disable react/jsx-props-no-spreading */
import {
  AudienceType,
  MatchingCriteria,
  PartnerPermissionTypes,
} from '@/client/services/api/graphql/gql/graphql';
import { Collapse, Flex, HStack, Icon, Stack, Switch, Text } from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import { MdRadioButtonChecked, MdRadioButtonUnchecked } from 'react-icons/md';

import type { AssignmentFormData } from '../../../types/assignmentTypes';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function AssignmentAudienceExtendAccessForm() {
  const { t } = useTranslation();
  const { watch, setValue, control } = useFormContext<AssignmentFormData>();
  const { audience, useExtendAccessAudienceFilter } = watch();

  const partnerPermissionItems = audience.filter((item) => item.type === AudienceType.PartnerType);

  const hasPartner = partnerPermissionItems.some(
    (item) => item.id === PartnerPermissionTypes.Partner,
  );
  const hasTenant = partnerPermissionItems.some(
    (item) => item.id === PartnerPermissionTypes.Tenant,
  );

  const defaultPartnerPermissionItem = {
    type: AudienceType.PartnerType,
    id: PartnerPermissionTypes.Tenant,
    matchingCriteria: MatchingCriteria.MatchAll,
    name: AudienceType.PartnerType,
    segmentationType: AudienceType.PartnerType,
  };

  // Handle initial state
  useEffect(() => {
    if (partnerPermissionItems.length > 0 && !useExtendAccessAudienceFilter) {
      setValue('useExtendAccessAudienceFilter', true);
    }
  }, []);

  const handleSwitchChange = (checked: boolean) => {
    if (checked) {
      if (partnerPermissionItems.length === 0) {
        setValue('audience', [
          ...audience,
          { ...defaultPartnerPermissionItem, id: PartnerPermissionTypes.Partner },
        ]);
      }
    } else {
      setValue(
        'audience',
        audience.filter((item) => item.type !== AudienceType.PartnerType),
      );
    }
    setValue('useExtendAccessAudienceFilter', checked);
  };

  const options = [
    {
      label: t('admin.assignments.audience.extend.extendAccess.instanceOnly'),
      value: PartnerPermissionTypes.Tenant,
    },
    {
      label: t('admin.assignments.audience.extend.extendAccess.allExtendAccounts'),
      value: PartnerPermissionTypes.Partner,
    },
    {
      label: t('admin.assignments.audience.extend.extendAccess.bothExtendAndInstance'),
      value: PartnerPermissionTypes.PartnerAndTenant,
    },
  ];

  const getSelectedOption = () => {
    if (hasPartner && hasTenant) {
      return PartnerPermissionTypes.PartnerAndTenant;
    }
    if (hasPartner) {
      return PartnerPermissionTypes.Partner;
    }
    if (hasTenant) {
      return PartnerPermissionTypes.Tenant;
    }
    return null;
  };

  const handleOptionClick = (optionValue: PartnerPermissionTypes) => {
    const newAudience = audience.filter((item) => item.type !== AudienceType.PartnerType);

    if (optionValue === PartnerPermissionTypes.PartnerAndTenant) {
      // Add both Partner and Tenant permissions
      newAudience.push(
        { ...defaultPartnerPermissionItem, id: PartnerPermissionTypes.Partner },
        { ...defaultPartnerPermissionItem, id: PartnerPermissionTypes.Tenant },
      );
    } else {
      // Add single permission
      newAudience.push({ ...defaultPartnerPermissionItem, id: optionValue });
    }

    setValue('audience', newAudience);
  };

  return (
    <Stack>
      <Flex alignItems="center" justifyContent="space-between">
        <Stack spacing={0.5}>
          <Text variant="createLabel">{t('admin.assignments.audience.extend.extendAccess')}</Text>
          <Text variant="createHelpText">
            {t('admin.assignments.audience.extend.extendAccess.helpText')}
          </Text>
        </Stack>
        <Controller
          name="useExtendAccessAudienceFilter"
          control={control}
          render={({ field: { value, ...field } }) => (
            <Switch
              {...field}
              variant="create"
              isChecked={value}
              onChange={(e) => handleSwitchChange(e.target.checked)}
            />
          )}
        />
      </Flex>

      <Collapse in={useExtendAccessAudienceFilter}>
        <HStack flexWrap="wrap" gap={2}>
          {options.map((option) => {
            const isSelected = getSelectedOption() === option.value;

            return (
              <HStack
                key={option.value}
                paddingX={4}
                paddingY={3}
                border="1px solid"
                borderRadius="6px"
                borderColor={isSelected ? 'warmNeutral.400' : 'warmNeutral.200'}
                color={isSelected ? 'baseBlack' : 'neutral.700'}
                _hover={{
                  cursor: 'pointer',
                  borderColor: 'warmNeutral.400',
                  color: 'baseBlack',
                }}
                onClick={() => handleOptionClick(option.value)}
              >
                <Icon
                  as={isSelected ? MdRadioButtonChecked : MdRadioButtonUnchecked}
                  color={isSelected ? 'warmNeutral.400' : 'warmNeutral.200'}
                />

                <Text fontSize="14px" fontWeight={500}>
                  {option.label}
                </Text>
              </HStack>
            );
          })}
        </HStack>
      </Collapse>
    </Stack>
  );
}
